import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import AlertDialog from '../../components/AlertDialog';
import NewsEditModal from './NewsEditModal';
import {
  NEWS_HOME_SHOW_OPTIONS,
  NEWS_STATE,
  NEWS_STATE_OPTIONS,
  NEWS_CATEGORY_OPTIONS,
  NEWS_PROVIDER_OPTIONS,
  buildNewsPageParams,
  formatHomeShow,
  formatNewsCategory,
  formatNewsProvider,
  formatNewsSource,
  formatNewsState,
} from './newsConstants';

const PAGE_SIZE = 20;

const EMPTY_FILTERS = {
  dataState: '',
  viewState: '',
  title: '',
  category: '',
  provider: '',
  startTimeInput: '',
  endTimeInput: '',
};

const TABLE_COLUMNS = [
  '標題（英語）',
  '圖片（英語）',
  '狀態',
  '是否在首頁顯示',
  '業務分類',
  '數據源',
  '新聞時間',
  '順序',
  '來源',
  '操作',
];

export default function NewsInfoPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [offShelfTarget, setOffShelfTarget] = useState(null);
  const [onShelfTarget, setOnShelfTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [reloadLoading, setReloadLoading] = useState(false);
  const [reloadAlert, setReloadAlert] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.newsPageQuery(buildNewsPageParams(pageNo, PAGE_SIZE, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.deleteNews(deleteTarget.id);
      setDeleteTarget(null);
      setMessage('已刪除');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleConfirmOffShelf() {
    if (!offShelfTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.patchNewsState({ id: offShelfTarget.id, state: NEWS_STATE.OFF });
      setOffShelfTarget(null);
      setMessage('已下架');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '下架失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleConfirmOnShelf() {
    if (!onShelfTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.patchNewsState({ id: onShelfTarget.id, state: NEWS_STATE.ON });
      setOnShelfTarget(null);
      setMessage('已發布');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '發布失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleNewsReload() {
    setReloadLoading(true);
    try {
      await adminApi.newsReload();
      setReloadAlert({
        title: '提示',
        message: '已觸發新聞爬蟲，請稍後重新整理列表查看結果。',
      });
    } catch (err) {
      setReloadAlert({
        title: '無法執行',
        message: err.message || '觸發失敗，請稍後再試或聯繫運維人員。',
      });
    } finally {
      setReloadLoading(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>新聞資訊</h2>
        <div className="section-header__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleNewsReload}
            disabled={reloadLoading}
          >
            {reloadLoading ? '觸發中…' : '手動爬蟲'}
          </button>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </div>

      <p className="form-hint">新聞爬蟲於每日 AM 02:00:00 自動執行</p>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="newsFilterState">狀態</label>
          <select
            id="newsFilterState"
            value={filters.dataState}
            onChange={(e) => updateFilter('dataState', e.target.value)}
          >
            {NEWS_STATE_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="newsFilterHome">是否在首頁顯示</label>
          <select
            id="newsFilterHome"
            value={filters.viewState}
            onChange={(e) => updateFilter('viewState', e.target.value)}
          >
            {NEWS_HOME_SHOW_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="newsFilterCategory">業務分類</label>
          <select
            id="newsFilterCategory"
            value={filters.category}
            onChange={(e) => updateFilter('category', e.target.value)}
          >
            {NEWS_CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="newsFilterProvider">數據源</label>
          <select
            id="newsFilterProvider"
            value={filters.provider}
            onChange={(e) => updateFilter('provider', e.target.value)}
          >
            {NEWS_PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="newsFilterTitle">標題（英語）</label>
          <input
            id="newsFilterTitle"
            type="text"
            value={filters.title}
            onChange={(e) => updateFilter('title', e.target.value)}
            placeholder="請輸入英語標題"
          />
        </div>
        <div className="form-field">
          <label htmlFor="newsFilterStart">新聞時間起</label>
          <input
            id="newsFilterStart"
            type="datetime-local"
            value={filters.startTimeInput}
            onChange={(e) => updateFilter('startTimeInput', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="newsFilterEnd">新聞時間迄</label>
          <input
            id="newsFilterEnd"
            type="datetime-local"
            value={filters.endTimeInput}
            onChange={(e) => updateFilter('endTimeInput', e.target.value)}
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td className="cell-wrap">{row.enTitle || '—'}</td>
                  <td>
                    {row.enImage ? (
                      <img src={resolveAdminMediaUrl(row.enImage)} alt="" className="news-thumb-preview" />
                    ) : (
                      '未設定'
                    )}
                  </td>
                  <td>{formatNewsState(row.state)}</td>
                  <td>{formatHomeShow(row.isHomeShow)}</td>
                  <td>{formatNewsCategory(row.category)}</td>
                  <td>{formatNewsProvider(row.provider)}</td>
                  <td>{formatDateTime(row.newsTime)}</td>
                  <td>{row.sort ?? '—'}</td>
                  <td>{formatNewsSource(row.source)}</td>
                  <td>
                    <div className="table-actions table-actions--news">
                      <div className="table-actions__row">
                        <button type="button" className="btn btn-success btn-sm" onClick={() => openEdit(row)}>
                          修改
                        </button>
                        <button type="button" className="btn btn-danger-outline btn-sm" onClick={() => setDeleteTarget(row)}>
                          刪除
                        </button>
                      </div>
                      <div className="table-actions__row">
                        {row.state === NEWS_STATE.ON ? (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setOffShelfTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--danger" aria-hidden="true">
                              ✕
                            </span>
                            下架
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setOnShelfTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--success" aria-hidden="true">
                              ✓
                            </span>
                            發布
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, filters)} />

      <NewsEditModal
        open={editOpen}
        newsId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={(wasEdit) => {
          setMessage(wasEdit ? '已更新' : '已新增');
          loadRecords(page, filters);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除新聞"
        message={`確定刪除「${deleteTarget?.enTitle || deleteTarget?.id || ''}」？`}
        confirmLabel="刪除"
        confirming={actionLoading}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(offShelfTarget)}
        title="下架新聞"
        message={`確定下架「${offShelfTarget?.enTitle || offShelfTarget?.id || ''}」？`}
        confirmLabel="下架"
        confirming={actionLoading}
        onConfirm={handleConfirmOffShelf}
        onClose={() => setOffShelfTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(onShelfTarget)}
        title="發布新聞"
        message={`確定發布「${onShelfTarget?.enTitle || onShelfTarget?.id || ''}」？`}
        confirmLabel="發布"
        confirming={actionLoading}
        onConfirm={handleConfirmOnShelf}
        onClose={() => setOnShelfTarget(null)}
      />

      <AlertDialog
        open={Boolean(reloadAlert)}
        title={reloadAlert?.title}
        message={reloadAlert?.message}
        onClose={() => setReloadAlert(null)}
      />
    </div>
  );
}
